// Thin wrapper around the vendored GameChanger Sabertooth engine (./sabertooth.js).
//
// sabertooth.js is a compiled webpack bundle that, when loaded with `window` defined,
// exposes `window.controllers` (and `window.transcoderConfigFromStream`). We lazy-load it
// here so the ~1.2MB bundle is only fetched on the Box Score / Plays tabs.
//
// GC stream events are in the COMPACT format (every event carries `compactorAttributes`,
// pitches use `attributes.result`), so we use the compactor controller, replay the events
// with `pushBatch`, and read the resulting state via `latestState()`.
import * as Eden from "src/eden/content";

// TEMP kill switch (diagnostic): set to `false` to disable ALL sabertooth processing —
// no game-stream fetch, no sabertooth.js bundle load, no engine replay, and the Box Score
// and Plays tabs are hidden. Used to rule sabertooth out as the source of the local-dev
// slowdown. Flip back to `true` to re-enable.
export const SABERTOOTH_ENABLED = true;

// ---- Typed views of the sabertooth state accessor (only what the UI consumes) ----

export interface SaberSituation {
  inning: number;
  half: "top" | "bottom";
  balls: number;
  strikes: number;
  outs: number;
  bases: Record<string, string>;
  homeScore: number;
  awayScore: number;
}

export interface SaberPitch {
  result: string;
  eventId: string;
  resultingBalls: number;
  resultingStrikes: number;
  resultingOuts: number;
  template: string;
  createdAt: number;
}

export interface SaberParticipant {
  playerId: string;
  role: "batter" | "pitcher" | "fielder" | "runner";
  template?: string;
  perspective?: string;
  position?: string;
  base?: number;
  out?: boolean;
  scored?: boolean;
  committedError?: boolean;
}

/**
 * A between-pitch event within an at-bat: baserunning (steal, pickoff, wild pitch, caught
 * stealing, a runner scoring/advancing mid-count), a courtesy-runner swap, or a substitution.
 * These are what GameChanger interleaves into the play log alongside the pitch calls; the Plays
 * tab replays them at {@link beforePitchIndex} (the pitch they occurred before). `template` carries
 * `${playerId}` placeholders like the at-bat template; participants flagged `out` are the outs
 * recorded on this event (e.g. a caught-stealing runner), which the out tally must include.
 */
export interface SaberChildEvent {
  // sabertooth event code: "base_running" | "replace_runner" | "fill_position" | ...
  code: string;
  beforePitchIndex: number;
  template: string;
  participants: { playerId: string; out: boolean }[];
}

export interface SaberAtBat {
  pitches: SaberPitch[];
  result: { playType: string; situation: SaberSituation };
  participants: SaberParticipant[];
  // Between-pitch events (baserunning / substitutions). Only the codes the Plays tab surfaces are
  // kept (see pickAtBat); the pregame batting-order card is dropped at extraction.
  childEvents: SaberChildEvent[];
  headline: string;
  template: string;
  initialSituation: SaberSituation;
}

export interface SaberPlayByPlay {
  completedAtBats: SaberAtBat[];
  currentAtBat: SaberAtBat | null;
  currentBatter: string | null;
}

export type SaberStatLine = Record<string, number>;

export interface SaberTeamStats {
  stats: { offense: SaberStatLine; defense: SaberStatLine; general: SaberStatLine };
  players: Record<string, { stats: { offense: SaberStatLine; defense: SaberStatLine; general: SaberStatLine } }>;
}

export interface SaberBoxscoreSituation {
  battingOrder: string[][];
  pitcherHistory: { pitcherId: string; inning: number; firstAppearance: boolean }[];
  fielderHistory: Record<string, string[]>;
  courtesyRunners: unknown[];
}

export type SaberLinescore = Record<
  string,
  { runsPerInning: number[]; totalRuns: number; totalHits: number; totalErrors: number }
>;

export interface SaberState {
  scores: Record<string, number>;
  inning: number;
  half: "top" | "bottom";
  outs: number;
  atBatTeamId: string;
  inFieldTeamId: string;
  linescoreWithStatTotals: SaberLinescore;
  fullPlayByPlay: SaberPlayByPlay;
  // Winning/losing/save pitcher; only populated once the game is complete ("CPT").
  decisions?: { win?: unknown; loss?: unknown; save?: unknown };
  boxscoreSituation(teamId: string): SaberBoxscoreSituation;
  gameStatsForTeam(teamId: string): SaberTeamStats | undefined;
}

/**
 * A plain, serializable snapshot of the data the UI consumes — extracted up front for the home
 * and away teams. This is what we hand to React/components instead of the live `SaberState`.
 *
 * Critically, the `SaberState` accessor returned by `latestState()` holds a `controller` back-
 * reference to the entire sabertooth engine (~19MB once expanded, with heavy shared references).
 * Keeping that in React state froze the whole tab as soon as anything walked component state
 * (e.g. React DevTools serializing on every commit). The extracted snapshot is ~230KB of plain
 * data, so it's safe to store, pass as props, and serialize.
 */
export interface SaberGameSnapshot {
  scores: Record<string, number>;
  inning: number;
  half: "top" | "bottom";
  outs: number;
  atBatTeamId: string;
  inFieldTeamId: string;
  linescoreWithStatTotals: SaberLinescore;
  fullPlayByPlay: SaberPlayByPlay;
  // Keyed by team id; only the game's two teams are populated.
  teamStats: Record<string, SaberTeamStats | undefined>;
  boxscore: Record<string, SaberBoxscoreSituation | undefined>;
}

interface SaberContext {
  sport: string;
  ageGroup: string;
  inningsPerGame: number;
  playerNames: Record<string, unknown>;
  playByPlayMetadataKeys: string[];
}

interface SaberController {
  pushBatch(events: unknown[]): void;
  latestState(): SaberState;
}

interface SaberControllers {
  baseball: { usingCompactor: new (context: SaberContext) => SaberController };
}

// ---- Lazy loader ----

let controllersPromise: Promise<SaberControllers> | null = null;

function loadControllers(): Promise<SaberControllers> {
  if (!controllersPromise) {
    controllersPromise = import(
      // sabertooth.js is a side-effect-only webpack bundle (no ES exports); it sets
      // window.controllers when it runs. webpack loads it fine; TS just can't type it.
      // @ts-expect-error - not an ES module
      "./sabertooth.js"
    ).then(() => {
      const controllers = (window as unknown as { controllers?: SaberControllers }).controllers;
      if (!controllers?.baseball?.usingCompactor) {
        throw new Error("sabertooth.js loaded but window.controllers.baseball is unavailable");
      }
      return controllers;
    });
  }
  return controllersPromise;
}

export interface BuildGameStateOptions {
  // GC sport string ("baseball" | "softball" | ...). Softball still uses the baseball controller.
  sport: string;
  // Sabertooth age bucket: "youth" | "prep" | "college" | "adult" (only affects FIP/illegal-pitch).
  ageGroup?: string;
  inningsPerGame?: number;
}

export interface BuiltGameState {
  homeTeamId: string;
  awayTeamId: string;
  state: SaberGameSnapshot;
  // True once the game is decided (a winning/losing pitcher exists, or a decision event is in the
  // stream). Used to treat a game as final even when the summary's game_status is stale ("live").
  gameOver: boolean;
}

// ---- Snapshot extraction ----
//
// CRITICAL: the objects returned by `latestState()` and its accessors (`gameStatsForTeam`,
// `boxscoreSituation`, `fullPlayByPlay`, ...) are NOT plain data — they are live engine views
// backed by getters, including *back-reference* getters (an at-bat can hand you the whole game,
// a participant the player, the player all their at-bats, ...). Any generic deep walk of them
// (`JSON.stringify`, a recursive clone, React DevTools, ...) follows those getters and expands
// the graph combinatorially — runaway multi-GB allocation that grows until the tab is killed.
//
// So we NEVER recurse generically. Every helper below reads only named scalar leaves and known
// arrays of known-shaped objects, bounding the work (and the result) to the real data size.

const num = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) ? v : 0);
const str = (v: unknown): string => (typeof v === "string" ? v : "");

/** Copy only the finite-number entries of a sabertooth stat dict into a plain object. */
function pickStatLine(line: unknown): SaberStatLine {
  const out: SaberStatLine = {};
  if (line && typeof line === "object") {
    for (const [k, v] of Object.entries(line as Record<string, unknown>)) {
      if (typeof v === "number" && Number.isFinite(v)) out[k] = v;
    }
  }
  return out;
}

function pickStatGroup(g: unknown): SaberTeamStats["stats"] {
  const o = (g ?? {}) as Record<string, unknown>;
  return { offense: pickStatLine(o.offense), defense: pickStatLine(o.defense), general: pickStatLine(o.general) };
}

function pickTeamStats(ts: SaberTeamStats | undefined): SaberTeamStats | undefined {
  if (!ts) return undefined;
  const players: SaberTeamStats["players"] = {};
  for (const [id, p] of Object.entries(ts.players ?? {})) {
    players[id] = { stats: pickStatGroup((p as { stats?: unknown })?.stats) };
  }
  return { stats: pickStatGroup(ts.stats), players };
}

function pickBoxscore(b: SaberBoxscoreSituation | undefined): SaberBoxscoreSituation | undefined {
  if (!b) return undefined;
  const fielderHistory: Record<string, string[]> = {};
  for (const [id, positions] of Object.entries(b.fielderHistory ?? {})) {
    fielderHistory[id] = (Array.isArray(positions) ? positions : []).map(str);
  }
  return {
    battingOrder: (b.battingOrder ?? []).map((slot) => (slot ?? []).map(str)),
    pitcherHistory: (b.pitcherHistory ?? []).map((ph) => ({
      pitcherId: str(ph?.pitcherId),
      inning: num(ph?.inning),
      firstAppearance: !!ph?.firstAppearance,
    })),
    fielderHistory,
    courtesyRunners: [],
  };
}

function pickSituation(s: unknown): SaberSituation {
  const o = (s ?? {}) as Record<string, unknown>;
  const bases: Record<string, string> = {};
  if (o.bases && typeof o.bases === "object") {
    for (const [k, v] of Object.entries(o.bases as Record<string, unknown>)) bases[k] = str(v);
  }
  return {
    inning: num(o.inning),
    half: o.half === "bottom" ? "bottom" : "top",
    balls: num(o.balls),
    strikes: num(o.strikes),
    outs: num(o.outs),
    bases,
    homeScore: num(o.homeScore),
    awayScore: num(o.awayScore),
  };
}

function pickPitch(p: unknown): SaberPitch {
  const o = (p ?? {}) as Record<string, unknown>;
  return {
    result: str(o.result),
    eventId: str(o.eventId),
    resultingBalls: num(o.resultingBalls),
    resultingStrikes: num(o.resultingStrikes),
    resultingOuts: num(o.resultingOuts),
    template: str(o.template),
    createdAt: num(o.createdAt),
  };
}

function pickParticipant(p: unknown): SaberParticipant {
  const o = (p ?? {}) as Record<string, unknown>;
  return {
    playerId: str(o.playerId),
    role: o.role as SaberParticipant["role"],
    template: typeof o.template === "string" ? o.template : undefined,
    perspective: typeof o.perspective === "string" ? o.perspective : undefined,
    position: typeof o.position === "string" ? o.position : undefined,
    base: typeof o.base === "number" ? o.base : undefined,
    out: typeof o.out === "boolean" ? o.out : undefined,
    scored: typeof o.scored === "boolean" ? o.scored : undefined,
    committedError: typeof o.committedError === "boolean" ? o.committedError : undefined,
  };
}

// Child-event codes the Plays tab actually surfaces. Baserunning + substitutions; we drop the
// pregame roster/batting-order cards ("fill_lineup_index"/"goto_lineup_index") at extraction —
// GameChanger never shows them in the play log and they carry no outs, so keeping them would only
// bloat the snapshot. (The full-defense declaration that comes in as "fill_position" is kept here
// and suppressed at render time; see pitchSequenceText.)
const RENDERABLE_CHILD_CODES = new Set(["base_running", "replace_runner", "fill_position"]);

function pickChildEvent(c: unknown): SaberChildEvent {
  const o = (c ?? {}) as Record<string, unknown>;
  const participants = (Array.isArray(o.participants) ? o.participants : []).map((p) => {
    const po = (p ?? {}) as Record<string, unknown>;
    return { playerId: str(po.playerId), out: po.out === true };
  });
  return {
    code: str(o.code),
    beforePitchIndex: typeof o.beforePitchIndex === "number" ? o.beforePitchIndex : 0,
    template: str(o.template),
    participants,
  };
}

function pickAtBat(ab: unknown): SaberAtBat {
  const o = (ab ?? {}) as Record<string, unknown>;
  const result = (o.result ?? {}) as Record<string, unknown>;
  const childEvents = (Array.isArray(o.childEvents) ? o.childEvents : [])
    .filter((c) => RENDERABLE_CHILD_CODES.has(str((c as Record<string, unknown>)?.code)))
    .map(pickChildEvent);
  return {
    pitches: Array.isArray(o.pitches) ? o.pitches.map(pickPitch) : [],
    result: { playType: str(result.playType), situation: pickSituation(result.situation) },
    participants: Array.isArray(o.participants) ? o.participants.map(pickParticipant) : [],
    childEvents,
    headline: str(o.headline),
    template: str(o.template),
    initialSituation: pickSituation(o.initialSituation),
  };
}

function pickLinescore(ls: SaberLinescore | undefined, teamIds: string[]): SaberLinescore {
  const out: SaberLinescore = {};
  for (const id of teamIds) {
    const e = ls?.[id];
    if (e) {
      out[id] = {
        runsPerInning: (Array.isArray(e.runsPerInning) ? e.runsPerInning : []).map(num),
        totalRuns: num(e.totalRuns),
        totalHits: num(e.totalHits),
        totalErrors: num(e.totalErrors),
      };
    }
  }
  return out;
}

function pickNumberMap(m: unknown, keys: string[]): Record<string, number> {
  const out: Record<string, number> = {};
  const o = (m ?? {}) as Record<string, unknown>;
  for (const k of keys) if (k in o) out[k] = num(o[k]);
  return out;
}

/**
 * Builds the plain, bounded {@link SaberGameSnapshot} the UI consumes by reading only the specific
 * scalar fields off the live engine views — never deep-walking them. See the note at the top of
 * this section for why a generic clone explodes.
 */
function toSnapshot(live: SaberState, homeTeamId: string, awayTeamId: string): SaberGameSnapshot {
  const teamIds = [homeTeamId, awayTeamId];
  const pbp = live.fullPlayByPlay;
  return {
    scores: pickNumberMap(live.scores, teamIds),
    inning: num(live.inning),
    half: live.half === "bottom" ? "bottom" : "top",
    outs: num(live.outs),
    atBatTeamId: str(live.atBatTeamId),
    inFieldTeamId: str(live.inFieldTeamId),
    linescoreWithStatTotals: pickLinescore(live.linescoreWithStatTotals, teamIds),
    fullPlayByPlay: {
      completedAtBats: Array.isArray(pbp?.completedAtBats) ? pbp.completedAtBats.map(pickAtBat) : [],
      currentAtBat: pbp?.currentAtBat ? pickAtBat(pbp.currentAtBat) : null,
      currentBatter: typeof pbp?.currentBatter === "string" ? pbp.currentBatter : null,
    },
    teamStats: {
      [homeTeamId]: pickTeamStats(live.gameStatsForTeam(homeTeamId)),
      [awayTeamId]: pickTeamStats(live.gameStatsForTeam(awayTeamId)),
    },
    boxscore: {
      [homeTeamId]: pickBoxscore(live.boxscoreSituation(homeTeamId)),
      [awayTeamId]: pickBoxscore(live.boxscoreSituation(awayTeamId)),
    },
  };
}

interface RawEvent {
  code?: string;
  attributes?: { homeId?: string; awayId?: string };
}

/**
 * Replays a GC game stream through sabertooth and returns the resolved home/away team ids
 * (from the `set_teams` event) plus a plain {@link SaberGameSnapshot}. Returns null if the stream
 * has no events yet (e.g. a game that just went live before any scorekeeping).
 */
export async function buildGameState(
  stream: Eden.GameStream,
  options: BuildGameStateOptions
): Promise<BuiltGameState | null> {
  if (!stream?.events?.length) return null;

  const controllers = await loadControllers();

  const events: RawEvent[] = [...stream.events]
    .sort((a, b) => a.sequence_number - b.sequence_number)
    .map((e) => JSON.parse(e.event_data));

  const setTeams = events.find((e) => e.code === "set_teams");
  if (!setTeams?.attributes?.homeId || !setTeams.attributes.awayId) return null;

  const context: SaberContext = {
    sport: options.sport === "softball" ? "softball" : "baseball",
    ageGroup: options.ageGroup ?? "youth",
    inningsPerGame: options.inningsPerGame ?? 7,
    playerNames: {},
    playByPlayMetadataKeys: [],
  };

  const controller = new controllers.baseball.usingCompactor(context);
  controller.pushBatch(events);

  const live = controller.latestState();
  // The game is final once a pitcher decision exists — sabertooth only assigns win/loss when the
  // game is complete ("CPT"). Check both the engine state and the raw stream (the summary's
  // game_status can stay "live"/"new" after a game actually ends, e.g. a mercy-rule finish).
  const decisions = live.decisions;
  const gameOver =
    !!(decisions && (decisions.win || decisions.loss)) ||
    events.some((e) => e.code === "win" || e.code === "loss" || e.code === "pitcher_decision");

  const homeTeamId = setTeams.attributes.homeId;
  const awayTeamId = setTeams.attributes.awayId;
  return { homeTeamId, awayTeamId, state: toSnapshot(live, homeTeamId, awayTeamId), gameOver };
}
